import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ANNOUNCEMENT_BANNER_BODY,
  ANNOUNCEMENT_BANNER_ENABLED,
  ANNOUNCEMENT_BANNER_LEAD,
  ANNOUNCEMENT_BANNER_PUBLIC,
  ANNOUNCEMENT_BANNER_STORAGE_KEY,
} from '../constants/announcementBanner';
import { useAuth } from '../contexts/AuthContext';
import { isInternalTesterAccount } from '../utils/internalTester';

const OFFSET_VAR = '--prairi-announcement-offset';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(ANNOUNCEMENT_BANNER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function clearOffset(): void {
  document.documentElement.style.setProperty(OFFSET_VAR, '0px');
}

/**
 * Site-wide v3.1 announcement. Soft-launch: admin/tester only until PUBLIC is true.
 * Dismiss (x) persists across sessions via localStorage.
 */
const AnnouncementBanner: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const allowedAudience = ANNOUNCEMENT_BANNER_PUBLIC || isAdmin || isInternalTesterAccount(user);

  useEffect(() => {
    if (!ANNOUNCEMENT_BANNER_ENABLED || !allowedAudience || wasDismissed()) {
      setVisible(false);
      clearOffset();
      return;
    }
    setVisible(true);
  }, [allowedAudience]);

  useLayoutEffect(() => {
    if (!visible) {
      clearOffset();
      return;
    }

    const el = rootRef.current;
    if (!el) return;

    const syncOffset = () => {
      document.documentElement.style.setProperty(OFFSET_VAR, `${el.offsetHeight}px`);
    };
    syncOffset();

    const observer = new ResizeObserver(syncOffset);
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearOffset();
    };
  }, [visible]);

  const dismiss = () => {
    try {
      localStorage.setItem(ANNOUNCEMENT_BANNER_STORAGE_KEY, '1');
    } catch {
      // Still hide for this session if storage is blocked.
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label="Announcement"
      className="sticky top-0 z-[100000] w-full"
      style={{ backgroundColor: '#f5f5f4', fontFamily: 'Rubik, sans-serif' }}
    >
      <div className="relative flex items-center justify-center px-9 py-2 text-center sm:px-12 sm:py-2.5">
        <p className="max-w-[min(52rem,calc(100%-2.75rem))] text-[13px] leading-snug text-[#141414] sm:text-sm md:text-base">
          <span className="font-bold">{ANNOUNCEMENT_BANNER_LEAD}</span>{' '}
          <span className="font-normal">{ANNOUNCEMENT_BANNER_BODY}</span>
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[#444444] hover:text-[#141414] sm:right-5"
        >
          <span className="text-xl leading-none" aria-hidden="true">
            ×
          </span>
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
